import type { JobItemDto } from "@artha/api-contracts";
import { GeoResponseSchema } from "@artha/api-contracts";
import { normalizeCountryCode } from "@artha/utils";
import ct from "countries-and-timezones";
import { detectIncognito } from "detectincognitojs";

import { apiFetch } from "@/lib/apiClient";

export type GeoCountry = {
  code: string;
  name: string;
};

export type RestrictionState = {
  isRestrictedByCountry: boolean;
  isRestrictedByVpn: boolean;
  isRestrictedByIncognito: boolean;
  seekerCountry?: GeoCountry;
  jobCountry?: GeoCountry;
};

export type ViewerGeo = {
  country: GeoCountry | null;
  timezone?: string;
  city?: string;
  state?: string;
  isVpn?: boolean;
  isProxy?: boolean;
  isHosting?: boolean;
};

// Temporary kill switch: geo, VPN, and incognito restrictions are disabled globally.
// Flip back to false to re-enable them.
export const isGeoRestrictionDisabled = () => false;

// Creator country codes where VPN/proxy restrictions are enforced (not allowed).
export const VPN_RESTRICTED_CREATOR_COUNTRIES = ["IN"];

/**
 * Check if the creator's country is configured to restrict VPN/proxy connections.
 */
export const isCreatorVpnRestricted = (
  creatorCountries?: string | string[]
): boolean => {
  if (!creatorCountries) return false;
  const countries = Array.isArray(creatorCountries) ? creatorCountries : [creatorCountries];
  const normalized = countries.map((country) => country.trim().toUpperCase());
  return normalized.some((country) =>
    VPN_RESTRICTED_CREATOR_COUNTRIES.includes(country)
  );
};

export const normalizeCountryForRestriction = (
  value?: string | null
): GeoCountry | null => {
  const trimmed = value?.trim();
  if (!trimmed) return null;

  const normalized = normalizeCountryCode(trimmed.toLowerCase());
  if (/^[A-Z]{2}$/.test(normalized)) {
    return {
      code: normalized,
      name: displayCountryName(normalized) || trimmed,
    };
  }

  const displayCode = getCountryCodeFromDisplayName(trimmed);
  if (displayCode) {
    return {
      code: displayCode,
      name: displayCountryName(displayCode) || trimmed,
    };
  }

  return {
    code: normalized.toLowerCase(),
    name: trimmed,
  };
};

export const getJobCountryForRestriction = (job: JobItemDto) =>
  normalizeCountryForRestriction(job.country);

export const isCountryMismatch = (
  seekerCountry?: GeoCountry | null,
  jobCountry?: GeoCountry | null
) => {
  if (!seekerCountry || !jobCountry) return false;
  return seekerCountry.code.toLowerCase() !== jobCountry.code.toLowerCase();
};

export const isVpnActive = (
  countryCode?: string | null,
  browserTimezoneOverride?: string
) => {
  const normalizedCountryCode = countryCode?.trim().toUpperCase();
  if (!normalizedCountryCode) return false;

  try {
    const browserTimezone =
      browserTimezoneOverride ||
      Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (!browserTimezone) return false;
    const normalizedBrowserTimezone = normalizeTimezoneAlias(browserTimezone);

    // Skip VPN check for UTC/GMT timezones to prevent false positives (e.g. developer environments, privacy-focused browsers, or fallback configs)
    if (
      normalizedBrowserTimezone === "UTC" ||
      normalizedBrowserTimezone === "GMT" ||
      normalizedBrowserTimezone === "Etc/UTC" ||
      normalizedBrowserTimezone === "Etc/GMT"
    ) {
      return false;
    }

    const country = ct.getCountry(normalizedCountryCode);
    if (!country?.timezones?.length) return false;
    const countryTimezones = new Set(
      (country.timezones as string[]).map(normalizeTimezoneAlias)
    );

    return !countryTimezones.has(normalizedBrowserTimezone);
  } catch {
    return false;
  }
};

export const getViewerGeo = async () => {
  try {
    const response = await apiFetch("/geo");
    if (!response.ok) return null;

    const parsed = GeoResponseSchema.safeParse((response.data as { data?: unknown })?.data);
    if (!parsed.success) return null;

    const geo = parsed.data.geo;
    const country = normalizeCountryForRestriction(geo?.country);

    return {
      country: country ? {
        ...country,
        name: geo?.countryDetail?.names?.en || country.name,
      } : null,
      timezone: geo?.timezone,
      city: geo?.city || undefined,
      state: geo?.region || undefined,
      isVpn: geo?.isVpn || false,
      isProxy: geo?.isProxy || false,
      isHosting: geo?.isHosting || false,
    };
  } catch (err) {
    console.error("[Geolocation] Failed to fetch viewer geo:", err);
    return null;
  }
};

const displayCountryName = (countryCode: string) => {
  try {
    return new Intl.DisplayNames(["en"], { type: "region" }).of(countryCode);
  } catch {
    return undefined;
  }
};

const getCountryCodeFromDisplayName = (countryName: string) => {
  const supportedValuesOf = (
    Intl as typeof Intl & { supportedValuesOf?: (key: string) => string[] }
  ).supportedValuesOf;
  const normalizedName = countryName.trim().toLowerCase();

  try {
    return supportedValuesOf?.("region").find(
      (code) => displayCountryName(code)?.toLowerCase() === normalizedName
    );
  } catch {
    return undefined;
  }
};

const normalizeTimezoneAlias = (timezone: string) => {
  const timezoneInfo = ct.getTimezone(timezone);
  return timezoneInfo?.aliasOf || timezoneInfo?.name || timezone;
};

export const isIncognitoActive = async (): Promise<boolean> => {
  if (typeof window === "undefined") return false;
  try {
    const result = await detectIncognito();
    // return result.isPrivate;
    return false; // reerted default to false
  } catch (error) {
    console.error("Error detecting incognito mode:", error);
    return false;
  }
};
